import { ReportDefinition } from '../models/report-definition';
import { ReportRowVm } from '../models/report-row-vm';
import {
   getFinancialPostingCashierId,
   getFinancialPostingDate,
   getFinancialPostingGuestName,
   getFinancialPostingReason,
   getFinancialPostingReceipt,
   getFinancialPostingRoom,
   getFinancialPostingSource,
   getFinancialPostingTime,
   getFinancialPostingTransactionCode,
   getFinancialPostingTransactionDescription,
   getFinancialPostingTransactionDisplay,
   getFinancialPostingUserDisplay
} from '../utils/financial-posting.utils';
import {
   getFirstBoolean,
   getFirstNumber,
   getFirstString
} from '../utils/report-field.utils';
import {
   formatCurrency,
   formatFlexibleDate,
   formatTime
} from '../utils/report-formatting.utils';
import { getRealTyallsRowId } from '../utils/report-row-id.utils';

const LAYOUT_DETAILED_REFUNDS = 'detailed-refunds';
const LAYOUT_REFUNDS_BY_USER = 'refunds-by-user';

type PaymentRefundRow = {
   rowId: string;
   date: string;
   dateRaw: string;
   time: string;
   room: string;
   guestName: string;
   method: string;
   transactionCode: string;
   transactionDescription: string;
   reason: string;
   receipt: string;
   amount: string;
   amountRaw: number;
   user: string;
   cashierId: string;
   reservationId: string;
   confirmationNumber: string;
   refundedCardNo: string;
   refundedPayMethod: string;
   cardMatch: string;
   hasRealTyallsRowId: boolean;
};

type RefundUserSummary = {
   user: string;
   count: number;
   totalAmount: number;
   sourceRowIds: string[];
};

function getRowId(row: unknown): string {
   const realTyallsRowId = getRealTyallsRowId(row);

   if (realTyallsRowId) {
      return realTyallsRowId;
   }

   return (
      getFirstString(row, [
         'ReceiptNumber',
         'receiptNumber',
         'ReceiptNo',
         'receiptNo',
         'TransactionNo',
         'transactionNo',
         'Id',
         'id'
      ]) ||
      `payment-refund:${getFirstString(row, ['Date', 'date'])}:${getFirstString(row, [
         'RoomNumber',
         'roomNumber'
      ])}:${getFirstString(row, ['TransactionCode', 'transactionCode'])}`
   );
}

function amountFromMappedPostingDollars(row: unknown): number | null {
   const credit = getFirstNumber(row, [
      'Credit',
      'credit',
      'CreditAmount',
      'creditAmount',
      'CreditAmount.Amount',
      'creditAmount.amount'
   ]);

   if (credit !== null && credit < 0) {
      return credit;
   }

   const debit = getFirstNumber(row, [
      'Debit',
      'debit',
      'DebitAmount',
      'debitAmount',
      'DebitAmount.Amount',
      'debitAmount.amount'
   ]);

   if (debit !== null && debit > 0) {
      return -Math.abs(debit);
   }

   const amount = getFirstNumber(row, ['Amount', 'amount', 'AmountRaw', 'amountRaw']);

   return amount !== null && amount < 0 ? amount : null;
}

function amountFromOraclePostingDollars(row: unknown): number | null {
   const amount = getFirstNumber(row, [
      'amount.amount',
      'postedAmount.amount',
      'Amount',
      'amount'
   ]);

   if (amount !== null && amount < 0) {
      return amount;
   }

   const debitAmount = getFirstNumber(row, [
      'debitAmount.amount',
      'DebitAmount.Amount',
      'DebitAmount',
      'debitAmount'
   ]);

   if (debitAmount !== null && debitAmount > 0) {
      return -Math.abs(debitAmount);
   }

   const creditAmount = getFirstNumber(row, [
      'creditAmount.amount',
      'CreditAmount.Amount',
      'CreditAmount',
      'creditAmount'
   ]);

   if (creditAmount !== null && creditAmount < 0) {
      return creditAmount;
   }

   return null;
}

function getMethod(row: unknown): string {
   const transactionDescription = getFinancialPostingTransactionDescription(row);

   if (transactionDescription) {
      return getFinancialPostingTransactionDisplay(row);
   }

   const transactionCode = getFinancialPostingTransactionCode(row);

   return (
      getFirstString(row, [
         'PaymentType',
         'paymentType',
         'PaymentGroup',
         'paymentGroup',
         'PaymentSubGroup',
         'paymentSubGroup'
      ]) || transactionCode
   );
}

function getReason(row: unknown): string {
   return getFinancialPostingReason(row, [
      [
         'Reason',
         'reason',
         'Description',
         'description',
         'Remark',
         'remark',
         'Reference',
         'reference',
         'SupplementReferenceCreditCardNo',
         'supplementReferenceCreditCardNo'
      ]
   ]);
}

function getReceipt(row: unknown): string {
   return getFinancialPostingReceipt(row, [
      'ReceiptNumber',
      'receiptNumber',
      'ReceiptNo',
      'receiptNo',
      'TransactionNo',
      'transactionNo'
   ]);
}

/** Only display a masked card identifier, including for historical report data. */
function getRefundCardDisplay(row: unknown): string {
   const card = getFirstString(row, ['RefundedCardNo', 'refundedCardNo']);
   const digits = card.replace(/\D/g, '');

   // Enrichment supplies a masked card number. Guard against an unexpected
   // unmasked PAN in older/imported report data before showing it in the table.
   if (digits.length > 4 && !/[xX*•]/.test(card)) {
      return `•••• ${digits.slice(-4)}`;
   }

   return card;
}

function getCardMatchDisplay(row: unknown): string {
   const cardMatch = getFirstBoolean(row, [
      'RefundedCardMatchesPaymentCard',
      'refundedCardMatchesPaymentCard'
   ]);

   if (cardMatch === true) {
      return 'Matched';
   }

   if (cardMatch === false) {
      return 'Not Matched';
   }

   return 'Not Available';
}

function normaliseRefundRows(
   sourceRows: unknown[],
   isOracle: boolean
): PaymentRefundRow[] {
   const rows: PaymentRefundRow[] = [];

   for (const sourceRow of sourceRows) {
      const amountRaw = isOracle
         ? amountFromOraclePostingDollars(sourceRow)
         : amountFromMappedPostingDollars(sourceRow);

      if (amountRaw === null || amountRaw >= 0) {
         continue;
      }

      const dateRaw = getFinancialPostingDate(sourceRow);

      rows.push({
         rowId: getRowId(sourceRow),
         date: formatFlexibleDate(dateRaw),
         dateRaw,
         time: formatTime(getFinancialPostingTime(sourceRow)),
         room: getFinancialPostingRoom(sourceRow),
         guestName: getFinancialPostingGuestName(sourceRow),
         method: getMethod(sourceRow),
         transactionCode: getFinancialPostingTransactionCode(sourceRow),
         transactionDescription: getFinancialPostingTransactionDescription(sourceRow),
         reason: getReason(sourceRow),
         receipt: getReceipt(sourceRow),
         amount: formatCurrency(amountRaw),
         amountRaw,
         user: getFinancialPostingUserDisplay(sourceRow),
         cashierId: getFinancialPostingCashierId(sourceRow),
         reservationId: getFirstString(sourceRow, ['ReservationId', 'reservationId']),
         confirmationNumber: getFirstString(sourceRow, [
            'ConfirmationNumber',
            'confirmationNumber'
         ]),
         refundedCardNo: getRefundCardDisplay(sourceRow),
         refundedPayMethod: getFirstString(sourceRow, [
            'RefundedPayMth',
            'refundedPayMth'
         ]),
         cardMatch: getCardMatchDisplay(sourceRow),
         hasRealTyallsRowId: !!getRealTyallsRowId(sourceRow)
      });
   }

   return rows;
}

function sortRefundRows(rows: PaymentRefundRow[]): PaymentRefundRow[] {
   return [...rows].sort((a, b) => {
      const dateCompare = a.dateRaw.localeCompare(b.dateRaw);

      if (dateCompare !== 0) {
         return dateCompare;
      }

      return a.time.localeCompare(b.time);
   });
}

function getRefundRows(reportJson: unknown): PaymentRefundRow[] {
   const { rows: sourceRows, isOracle } = getFinancialPostingSource(reportJson, [
      'PaymentRefunds'
   ]);

   return sortRefundRows(normaliseRefundRows(sourceRows, isOracle));
}

function buildRefundUserSummaries(rows: PaymentRefundRow[]): RefundUserSummary[] {
   const totals = new Map<string, RefundUserSummary>();

   for (const row of rows) {
      const user = row.user || 'Unknown';

      const current = totals.get(user) ?? {
         user,
         totalAmount: 0,
         count: 0,
         sourceRowIds: []
      };

      current.totalAmount += row.amountRaw;
      current.count += 1;

      if (row.hasRealTyallsRowId) {
         current.sourceRowIds.push(row.rowId);
      }

      totals.set(user, current);
   }

   return Array.from(totals.values()).sort((a, b) => {
      const aNumber = Number(a.user);
      const bNumber = Number(b.user);

      if (Number.isFinite(aNumber) && Number.isFinite(bNumber)) {
         return aNumber - bNumber;
      }

      return a.user.localeCompare(b.user);
   });
}

function flattenDetailedRefunds(reportJson: unknown): ReportRowVm[] {
   const refundRows = getRefundRows(reportJson);

   return refundRows.map((row) => ({
      rowId: row.rowId,
      isAnnotatable: row.hasRealTyallsRowId,
      cells: {
         rowType: 'transaction',
         date: row.date,
         dateRaw: row.dateRaw,
         time: row.time,
         room: row.room,
         guestName: row.guestName,
         method: row.method,
         transactionCode: row.transactionCode,
         transactionDescription: row.transactionDescription,
         reason: row.reason,
         receipt: row.receipt,
         amount: row.amount,
         amountRaw: row.amountRaw,
         user: row.user,
         cashierId: row.cashierId,
         reservationId: row.reservationId,
         confirmationNumber: row.confirmationNumber,
         refundedCardNo: row.refundedCardNo,
         refundedPayMethod: row.refundedPayMethod,
         cardMatch: row.cardMatch
      }
   }));
}

function flattenRefundsByUser(reportJson: unknown): ReportRowVm[] {
   const refundRows = getRefundRows(reportJson);
   const userSummaries = buildRefundUserSummaries(refundRows);

   return userSummaries.map((summary) => ({
      rowId: `payment-refunds:${LAYOUT_REFUNDS_BY_USER}:user:${summary.user}`,
      sourceRowIds: Array.from(new Set(summary.sourceRowIds)),
      isAnnotatable: false,
      cells: {
         rowType: 'userRefundSummary',
         user: summary.user,
         userRaw: summary.user,
         count: summary.count,
         totalRefunded: formatCurrency(summary.totalAmount),
         totalRefundedRaw: summary.totalAmount
      }
   }));
}

export const PAYMENT_REFUNDS_DEF: ReportDefinition = {
   reportType: 'PaymentRefunds',
   version: 11,
   title: 'Payment Refunds',

   sorting: {
      enabled: true,
      persist: true
   },

   header: {
      title: 'Payment Refunds'
   },

   rowChecks: {
      enabled: false,
      includeNestedRows: false,
      selectableRowTypes: ['transaction']
   },

   annotations: false,

   columns: [
      {
         key: 'date',
         header: 'TRN DATE',
         width: '105px',
         sort: {
            type: 'date',
            getValue: (row) => row.cells['dateRaw']
         }
      },
      {
         key: 'room',
         header: 'ROOM',
         width: '80px',
         sort: { type: 'string' }
      },
      {
         key: 'guestName',
         header: 'GUEST NAME',
         width: '160px',
         sort: { type: 'string' }
      },
      {
         key: 'method',
         header: 'METHOD',
         width: '150px',
         sort: { type: 'string' }
      },
      {
         key: 'reason',
         header: 'REASON',
         width: '360px',
         maxChars: 130,
         sort: { type: 'string' }
      },
      {
         key: 'refundedCardNo',
         header: 'REFUND CARD',
         width: '150px',
         sort: { type: 'string' }
      },
      {
         key: 'amount',
         header: 'AMOUNT',
         width: '115px',
         sort: {
            type: 'number',
            getValue: (row) => row.cells['amountRaw'],
            defaultDirection: 'asc'
         }
      },
      {
         key: 'user',
         header: 'USER',
         width: '140px',
         sort: { type: 'string' }
      }
   ],

   filter: {
      fields: [
         {
            key: 'date',
            label: 'Transaction Date',
            type: 'date',
            getValue: (row) => row.cells['dateRaw']
         },
         { key: 'room', label: 'Room', type: 'string' },
         { key: 'guestName', label: 'Guest Name', type: 'string' },
         { key: 'method', label: 'Method', type: 'string' },
         { key: 'reason', label: 'Reason', type: 'string' },
         { key: 'refundedCardNo', label: 'Refund Card', type: 'string' },
         { key: 'receipt', label: 'Receipt', type: 'string' },
         {
            key: 'amount',
            label: 'Amount',
            type: 'number',
            getValue: (row) => row.cells['amountRaw']
         },
         { key: 'user', label: 'User', type: 'string' }
      ]
   },

   flattenRows: flattenDetailedRefunds,

   layouts: [
      {
         id: LAYOUT_DETAILED_REFUNDS,
         label: 'Detailed Refunds',
         rowDetails: {
            enabled: true,
            selectableRowTypes: ['transaction'],
            fields: [
               {
                  key: 'cashierId',
                  label: 'Cashier ID'
               },
               {
                  key: 'refundedPayMethod',
                  label: 'Refund Payment Method'
               },
               {
                  key: 'receipt',
                  label: 'Receipt'
               },
               {
                  key: 'reservationId',
                  label: 'Reservation ID'
               },
               {
                  key: 'cardMatch',
                  label: 'Card Match',
                  visible: (row) =>
                     String(row.cells['transactionDescription'] ?? '')
                        .trim()
                        .toLowerCase() !== 'cash'
               }
            ]
         },
         annotations: {
            enabled: true,
            selectableRowTypes: ['transaction'],
            disabledMessage:
               'Row comments and row highlighting are not available for this report layout. Please use the main report comments panel to add comments for this report.'
         },
         rowChecks: {
            enabled: false,
            includeNestedRows: false,
            selectableRowTypes: ['transaction']
         },
         columns: [
            {
               key: 'date',
               header: 'TRN DATE',
               width: '105px',
               sort: {
                  type: 'date',
                  getValue: (row) => row.cells['dateRaw']
               }
            },
            {
               key: 'room',
               header: 'ROOM',
               width: '80px',
               sort: { type: 'string' }
            },
            {
               key: 'guestName',
               header: 'GUEST NAME',
               width: '160px',
               sort: { type: 'string' }
            },
            {
               key: 'method',
               header: 'METHOD',
               width: '150px',
               sort: { type: 'string' }
            },
            {
               key: 'reason',
               header: 'REASON',
               width: '360px',
               maxChars: 130,
               sort: { type: 'string' }
            },
            {
               key: 'refundedCardNo',
               header: 'REFUND CARD',
               width: '150px',
               sort: { type: 'string' }
            },
            {
               key: 'amount',
               header: 'AMOUNT',
               width: '115px',
               sort: {
                  type: 'number',
                  getValue: (row) => row.cells['amountRaw'],
                  defaultDirection: 'asc'
               }
            },
            {
               key: 'user',
               header: 'USER',
               width: '140px',
               sort: { type: 'string' }
            }
         ],
         filter: {
            fields: [
               {
                  key: 'date',
                  label: 'Transaction Date',
                  type: 'date',
                  getValue: (row) => row.cells['dateRaw']
               },
               { key: 'room', label: 'Room', type: 'string' },
               { key: 'guestName', label: 'Guest Name', type: 'string' },
               { key: 'method', label: 'Method', type: 'string' },
               { key: 'reason', label: 'Reason', type: 'string' },
               { key: 'refundedCardNo', label: 'Refund Card', type: 'string' },
               { key: 'receipt', label: 'Receipt', type: 'string' },
               {
                  key: 'amount',
                  label: 'Amount',
                  type: 'number',
                  getValue: (row) => row.cells['amountRaw']
               },
               { key: 'user', label: 'User', type: 'string' }
            ]
         },
         flattenRows: flattenDetailedRefunds
      },
      {
         id: LAYOUT_REFUNDS_BY_USER,
         label: 'Refunds by User',
         annotations: {
            enabled: false,
            disabledMessage:
               'Row comments and row highlighting are not available for Refunds by User because this layout is calculated from multiple rows. Please use the main report comments panel to add comments for this report.'
         },
         rowChecks: {
            enabled: false,
            includeNestedRows: false,
            selectableRowTypes: ['userRefundSummary']
         },
         columns: [
            {
               key: 'user',
               header: 'USER',
               width: '220px',
               sort: {
                  type: 'string',
                  getValue: (row) => row.cells['userRaw']
               }
            },
            {
               key: 'count',
               header: 'COUNT',
               width: '120px',
               sort: {
                  type: 'number',
                  defaultDirection: 'desc'
               }
            },
            {
               key: 'totalRefunded',
               header: 'TOTAL REFUNDED',
               width: '180px',
               sort: {
                  type: 'number',
                  getValue: (row) => row.cells['totalRefundedRaw'],
                  defaultDirection: 'asc'
               }
            }
         ],
         filter: {
            fields: [
               {
                  key: 'user',
                  label: 'User',
                  type: 'string',
                  getValue: (row) => row.cells['userRaw']
               },
               {
                  key: 'count',
                  label: 'Count',
                  type: 'number'
               },
               {
                  key: 'totalRefunded',
                  label: 'Total Refunded',
                  type: 'number',
                  getValue: (row) => row.cells['totalRefundedRaw']
               }
            ]
         },
         flattenRows: flattenRefundsByUser
      }
   ]
};
